import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Cell, Grid, InputGroup, Row, H5, Subheading, DangerButton } from '@fs/zion-ui'
import { useParams } from '@fs/zion-router'
import { ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import JSONTable from '../../app/JSON'
import { getMessageById } from '../../../services/dmqueuesService'
import useNotifications from '../../../hooks/useNotifications'

export default function SimpleMessageDetailsView({ deleteMessageById, backToMessagesList, buttonTemplates }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [message, setMessage] = useState(null)

  const pathParams = useParams()

  useEffect(() => {
    getMessageById(pathParams.messageId)
      .then((theMessage) => {
        setMessage(theMessage.data)
      })
      .catch((err) => {
        error(
          t('queues.errors.loadingMessage', {
            defaultValue: 'Error loading message {{messageId}}',
            messageId: pathParams.messageId,
          }),
          err
        )
      })
  }, [pathParams.messageId, error, t])

  if (!message) {
    return null
  }

  return (
    <Grid>
      {/* Row */}
      <Cell columns={12}>
        <H5 supplementary>
          <Row alignY="middle">
            <span>{message.queue}</span>
            <Button onClick={() => backToMessagesList()}>
              {t('queues.backToMessages', { defaultValue: `Back to ${message.queue}`, queue: message.queue })}
            </Button>
          </Row>
          <Subheading>{message.type}</Subheading>
        </H5>
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <Buttons
          message={message}
          backToMessagesList={backToMessagesList}
          buttonTemplates={buttonTemplates}
          deleteMessageById={deleteMessageById}
        />
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <JSONTable jsonContent={message} />
      </Cell>
    </Grid>
  )
}

function Buttons({ message, backToMessagesList, buttonTemplates, deleteMessageById }) {
  const [isPending, setIsPending] = useState(false)

  const runButtonCallback = (action) => {
    if (isPending) return
    setIsPending(true)
    Promise.resolve()
      .then(action)
      .finally(() => setIsPending(false))
  }

  return (
    <InputGroup>
      <Row>
        {buttonTemplates.map((buttonTemplate) => (
          <ButtonTemplateAction
            key={buttonTemplate.buttonText}
            buttonTemplate={buttonTemplate}
            message={message}
            isPending={isPending}
            runButtonCallback={runButtonCallback}
            backToMessagesList={backToMessagesList}
            deleteMessageById={deleteMessageById}
          />
        ))}
      </Row>
    </InputGroup>
  )
}

function ButtonTemplateAction({
  buttonTemplate,
  message,
  isPending,
  runButtonCallback,
  backToMessagesList,
  deleteMessageById,
}) {
  const handleDestructiveClick = () => {
    runButtonCallback(() => buttonTemplate.buttonCallback({ message }).then(() => backToMessagesList()))
  }

  const handleClick = () => {
    runButtonCallback(() =>
      buttonTemplate
        .buttonCallback({ message })
        .then(() => deleteMessageById({ message }))
        .then(() => backToMessagesList())
    )
  }

  if (buttonTemplate.isDestructive) {
    return (
      <DangerButton Icon={ContentDelete} disabled={isPending} onClick={handleDestructiveClick}>
        {buttonTemplate.buttonText}
      </DangerButton>
    )
  }

  return (
    <Button disabled={isPending} onClick={handleClick}>
      {buttonTemplate.buttonText}
    </Button>
  )
}
